import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { DestroyRef, inject, Signal, Injector } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { AbstractControl, ControlEvent, FormGroup, FormSubmittedEvent } from '@angular/forms';
import { signalStore, withMethods, withProps } from '@ngrx/signals';
import { BehaviorSubject, combineLatest, filter, map, Observable, Subject, tap } from 'rxjs';
import { RoutingService } from '@r1-compass-library/services';
import { LifecycleRuleset } from '@r1-compass-library/models/lifecycle-rules/lifecycle-ruleset.model';
import { ActivatedRoute } from '@angular/router';
import { rxEffect } from 'ngxtension/rx-effect';

import { ContentAsset, Play, TemplateCriteria } from '@r1-compass-library/models';
import { allEventsObservable } from '@r1-compass-library/utilities';

export const SaveGuardStore = signalStore(
    { providedIn: 'root' },
    withDevtools('saveGuard'),
    withProps(() => ({
        _routingService: inject(RoutingService),
        _injector: inject(Injector),
    })),
    withProps(() => {
        return {
            canDeactivate$: new BehaviorSubject<{ dirtied: boolean; valid: boolean; disableSave?: boolean }>({
                dirtied: false,
                valid: true,
            }),
            savedSoCanRoute$: new Subject<void>(),
        };
    }),
    withMethods(store => {
        function saveFormMarkUntouchAndPristine(form: AbstractControl) {
            return form.events.pipe(
                filter((event: ControlEvent): event is FormSubmittedEvent => event instanceof FormSubmittedEvent),
                tap(() => {
                    form.markAsUntouched();
                    form.markAsPristine();
                })
            );
        }

        function deriveCanDeactivate$(form: AbstractControl) {
            return allEventsObservable(form).pipe(
                map(formState => {
                    return {
                        dirtied: formState.dirty,
                        valid: formState.valid,
                    };
                })
            );
        }

        const saveFormHandler$ = (form: AbstractControl): Observable<FormSubmittedEvent> =>
            saveFormMarkUntouchAndPristine(form).pipe(
                tap(() => {
                    store.savedSoCanRoute$.next();
                })
            );

        const syncCanDeactivateToStore$ = (
            canDeactivate$: Observable<{ dirtied: boolean; valid: boolean; disableSave?: boolean }>,
            disableSave?: Signal<boolean>
        ) => {
            if (disableSave) {
                return combineLatest([canDeactivate$, toObservable(disableSave)]).pipe(
                    map(([{ dirtied, valid }, disableSaveVal]) => {
                        store.canDeactivate$.next({ dirtied, valid, disableSave: disableSaveVal });
                    })
                );
            }
            return canDeactivate$.pipe(
                map(({ dirtied, valid, disableSave: disableSaveVal }) => {
                    store.canDeactivate$.next({ dirtied, valid, disableSave: disableSaveVal });
                })
            );
        };

        /**
         * @description Specific centralized handler functions corresponding to each interface's `saveGuardHandlerFn`
         */
        const saveGuardHandlerFnFunctions = {
            formUnsavedChangesWarningEntityDefinition: (args: {
                form: FormGroup;
                canDeactivate$: Observable<{ dirtied: boolean; valid: boolean }>;
                params: Signal<string | null>;
                entity$: Observable<LifecycleRuleset | ContentAsset | TemplateCriteria | Play | undefined>;
                savedSoCanRoute$: Subject<void>;
                route: ActivatedRoute;
                disableSave?: Signal<boolean>;
            }) => {
                const routeToNewlyCreatedEntity$ = (
                    entityId: Signal<string | null>,
                    entity: Observable<LifecycleRuleset | ContentAsset | TemplateCriteria | Play | undefined>,
                    savedSoCanRoute$: Subject<void>,
                    route: ActivatedRoute
                ) => {
                    return combineLatest([toObservable(entityId), entity, savedSoCanRoute$]).pipe(
                        tap(([entityRouteId, ruleset, _]) => {
                            store._routingService.routeToNewlyCreatedEntity(ruleset, entityRouteId, route);
                        })
                    );
                };

                rxEffect(saveFormHandler$(args.form));
                rxEffect(syncCanDeactivateToStore$(args.canDeactivate$, args.disableSave));
                rxEffect(routeToNewlyCreatedEntity$(args.params, args.entity$, args.savedSoCanRoute$, args.route));
            },
            formUnsavedChangesWarningDivisionSettings: (args: {
                form: FormGroup;
                canDeactivate$: Observable<{ dirtied: boolean; valid: boolean }>;
                disableSave?: Signal<boolean>;
            }) => {
                rxEffect(saveFormHandler$(args.form));
                rxEffect(syncCanDeactivateToStore$(args.canDeactivate$, args.disableSave));
            },
            formUnsavedChangesWarningContentLibraryRules: (args: {
                saved$: Subject<void>;
                canDeactivate$: BehaviorSubject<{ dirtied: boolean; valid: boolean }>;
                disableSave?: Signal<boolean>;
            }) => {
                rxEffect(syncCanDeactivateToStore$(args.canDeactivate$, args.disableSave));
                rxEffect(args.saved$.pipe(tap(() => args.canDeactivate$.next({ dirtied: false, valid: true }))));
            },
            formUnsavedChangesWarningMisc: (args: {
                canDeactivate$: Observable<{ dirtied: boolean; valid: boolean }>;
                disableSave?: Signal<boolean>;
            }) => {
                rxEffect(syncCanDeactivateToStore$(args.canDeactivate$, args.disableSave));
            },
            formUnsavedChangesWarningAfterNgOnInit: (args: {
                form: FormGroup;
                canDeactivate$: BehaviorSubject<{ dirtied: boolean; valid: boolean; disableSave?: boolean }>;
                destroyRef$: DestroyRef;
                disableSave?: Signal<boolean>;
            }) => {
                rxEffect(
                    saveFormHandler$(args.form),
                    () => {
                        args.canDeactivate$.next({
                            dirtied: false,
                            valid: args.form.valid,
                        });
                    },
                    {
                        destroyRef: args.destroyRef$,
                    }
                );

                rxEffect(
                    deriveCanDeactivate$(args.form),
                    () => {
                        args.canDeactivate$.next({
                            dirtied: args.form.dirty,
                            valid: args.form.valid,
                        });
                    },
                    {
                        destroyRef: args.destroyRef$,
                    }
                );

                if (args.disableSave) {
                    rxEffect(
                        toObservable(args.disableSave, { injector: store._injector }),
                        disableSaveVal => {
                            const current = args.canDeactivate$.value;
                            args.canDeactivate$.next({
                                ...current,
                                disableSave: disableSaveVal,
                            });
                        },
                        {
                            destroyRef: args.destroyRef$,
                        }
                    );
                }
            },
        };

        return {
            formUnsavedChangesWarningEntityDefinition:
                saveGuardHandlerFnFunctions.formUnsavedChangesWarningEntityDefinition,
            formUnsavedChangesWarningDivisionSettings:
                saveGuardHandlerFnFunctions.formUnsavedChangesWarningDivisionSettings,
            formUnsavedChangesWarningContentLibraryRules:
                saveGuardHandlerFnFunctions.formUnsavedChangesWarningContentLibraryRules,
            formUnsavedChangesWarningMisc: saveGuardHandlerFnFunctions.formUnsavedChangesWarningMisc,
            formUnsavedChangesWarningAfterNgOnInit: saveGuardHandlerFnFunctions.formUnsavedChangesWarningAfterNgOnInit,
            deriveCanDeactivate$: deriveCanDeactivate$,
            saveFormMarkUntouchAndPristinePlayRecordSelection: saveFormMarkUntouchAndPristine,
        };
    })
);
